import React from "react";
import { useLocation, Link } from "react-router-dom";
import Footer from "@/components/auth/Footer";
import AuthProof from "@/components/auth/AuthProof";

/** Drives the per-screen vertical rhythm in vb-auth.css. */
export type AuthPage = "signin" | "create" | "forgot" | "check" | "reset" | "activate";

type AuthLayoutProps = {
    /** Which screen this is — selects the rhythm block. */
    page: AuthPage;
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    /** Status tile above the heading (outcome screens). */
    icon?: React.ReactNode;
    iconTone?: "pass" | "fail" | "brand";
    /** `split` = form + proof column (sign in / create). `narrow` = one card. */
    variant?: "split" | "narrow";
    children: React.ReactNode;
};

/**
 * The auth shell (aits-2.0 `.auth-page` / `.auth-shell`).
 *
 * Desktop `split` is the 1180px two-column card — form left, proof right.
 * `narrow` is the 560px single card the outcome screens use. Below 900px both
 * collapse to the same full-bleed phone layout: scrollable form above a
 * pinned footer strip.
 *
 * Wrapped in `.vb-root` so the surface picks up the v2 base (Figtree, ink,
 * canvas) — the auth routes sit outside the app shell.
 */
export default function AuthLayout({
    page,
    title,
    subtitle,
    icon,
    iconTone = "pass",
    variant = "split",
    children,
}: AuthLayoutProps) {
    const { pathname } = useLocation();
    // Only the entry points bounce back out to the marketing site.
    const isEntryRoute = ["/login", "/sign-up", "/forgot-password"].includes(pathname);
    const logoHref = isEntryRoute ? import.meta.env.VITE_LANDING_URL || "/" : "/";

    const logo = (
        <img className="auth-logo" src="/AITrustSignalSideBarLogo.svg" alt="AI Trust Signals" />
    );

    const header = (
        <>
            {isEntryRoute ? (
                <a href={logoHref} aria-label="Go to the AI Trust Signals home page">
                    {logo}
                </a>
            ) : (
                <Link to={logoHref} aria-label="Go to home">
                    {logo}
                </Link>
            )}

            {icon ? (
                <div className={`auth-icon-tile auth-icon-tile--${iconTone}`} aria-hidden="true">
                    {icon}
                </div>
            ) : null}

            {title || subtitle ? (
                <div className="auth-head">
                    {title ? <h1 id="authHeading">{title}</h1> : null}
                    {subtitle ? <p>{subtitle}</p> : null}
                </div>
            ) : null}
        </>
    );

    return (
        <div className="vb-root auth-page">
            <a className="auth-skip-link" href="#main">
                Skip to main content
            </a>

            {variant === "narrow" ? (
                <main
                    id="main"
                    tabIndex={-1}
                    className="auth-shell auth-shell--narrow"
                    data-auth-page={page}
                    aria-labelledby={title ? "authHeading" : undefined}
                >
                    <div className="auth-body">
                        {header}
                        {children}
                    </div>
                </main>
            ) : (
                <main id="main" tabIndex={-1} className="auth-shell" data-auth-page={page}>
                    <section
                        className="auth-form-col"
                        aria-labelledby={title ? "authHeading" : undefined}
                    >
                        {header}
                        {children}
                    </section>
                    <AuthProof />
                </main>
            )}

            <Footer />
        </div>
    );
}
